import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ResourceStatus } from 'libs.library_manager';
import { Button, Table, Text, Toggle, useLocalizeMessage } from 'libs.nucleus';
import { PageHeader, useBooleanState } from 'libs.react';
import { DEFAULT_DEBOUNCE_TIME } from 'libs.react_internal';
import { useChartsColumns } from './charts_columns.hook';
import { useCharts } from '../../hooks';
import { CreateChartModal } from '../create_chart_modal/create_chart_modal.component';
export const ChartsDashboard = () => {
    const navigate = useNavigate();
    const translate = useLocalizeMessage();
    const [isCreateChartModalOpen, openCreateChartModal, closeCreateChartModal] = useBooleanState();
    const [showArchived, setShowArchived] = useState(false);
    const [statusFilter, setStatusFilter] = useState();
    const [searchValue, setSearchValue] = useState('');
    const { charts, fetchCharts, archiveChart, isLoading } = useCharts();
    const handleArchiveChart = useCallback(async (chart) => {
        try {
            await archiveChart(chart.id);
            await fetchCharts(showArchived, statusFilter, searchValue);
        }
        catch (error) {
            // Error already handled in hook
        }
    }, [archiveChart, fetchCharts, showArchived, statusFilter, searchValue]);
    const chartsColumns = useChartsColumns({
        archiveChart: handleArchiveChart,
    });
    useEffect(() => {
        let isMounted = true;
        const loadCharts = async () => {
            try {
                await fetchCharts(showArchived, statusFilter, searchValue);
            }
            catch (error) {
                // Error already handled in hook
            }
        };
        if (isMounted) {
            loadCharts();
        }
        return () => {
            isMounted = false;
        };
    }, [fetchCharts, showArchived, statusFilter, searchValue]);
    const statusFilterFields = useMemo(() => [
        {
            fieldLabel: translate('Status'),
            fieldName: 'status',
            fieldKey: 'status',
            fieldWidth: 'sm',
            filterOptions: [
                { label: translate('In progress'), value: ResourceStatus.DRAFT },
                { label: translate('In review'), value: ResourceStatus.INREVIEW },
                { label: translate('Ready for use'), value: ResourceStatus.PUBLISHED },
            ],
        },
    ], [translate]);
    const handleStatusFilterChange = useCallback((filters) => {
        const selectedStatus = filters.status;
        if (selectedStatus && 'value' in selectedStatus) {
            setStatusFilter(selectedStatus.value);
            return;
        }
        setStatusFilter(undefined);
    }, []);
    const debouncedSearchChange = useMemo(() => {
        let timeoutId;
        return (value) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                setSearchValue(value);
            }, DEFAULT_DEBOUNCE_TIME);
        };
    }, []);
    const handleCreateChartSuccess = useCallback((chart) => {
        closeCreateChartModal();
        navigate(`/library/charts/${chart.id}`);
    }, [closeCreateChartModal, navigate]);
    const hasNoResults = !isLoading && charts.length === 0;
    const hasActiveSearch = searchValue.trim().length > 0;
    const hasActiveFilters = Boolean(statusFilter);
    const showEmptyState = hasNoResults && !showArchived && !hasActiveSearch && !hasActiveFilters;
    const headerOptions = showEmptyState
        ? []
        : [
            {
                name: translate('Create chart'),
                onClick: openCreateChartModal,
                variant: 'primary',
                disabled: isLoading,
            },
        ];
    if (showEmptyState) {
        return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: translate('Charts'), subtitle: translate('Create and manage visual representations of datasets.'), LinkComponent: Link }), _jsxs("div", { className: 'flex flex-col items-center justify-center border border-neutral-200 rounded-lg py-16', children: [_jsx(Text, { size: 'sm', className: 'text-neutral-500 mb-4', children: translate('No chart created.') }), _jsx(Button, { label: translate('Create chart'), variant: 'primary', onClick: openCreateChartModal })] }), isCreateChartModalOpen && (_jsx(CreateChartModal, { onClose: closeCreateChartModal, onSuccess: handleCreateChartSuccess }))] }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: translate('Charts'), subtitle: translate('Create and manage visual representations of datasets.'), LinkComponent: Link, options: headerOptions }), _jsx(Table, { isLoading: isLoading, data: charts, columns: chartsColumns, labelNoResults: translate('No charts found'), useInternalSearch: true, enableSorting: true, manualFiltering: true, onSearch: (value) => debouncedSearchChange(value || ''), onSearchClear: () => debouncedSearchChange(''), onFilter: handleStatusFilterChange, onFilterClear: () => setStatusFilter(undefined), filterFields: statusFilterFields, labelSearchField: translate('Search...'), customHeaderContent: _jsx("div", { className: 'flex gap-4 ml-auto', children: _jsx("div", { className: 'border-border-primary border-1 rounded-lg py-2 px-4 whitespace-nowrap min-w-max', children: _jsx(Toggle, { label: translate('Show archived'), checked: showArchived, onChange: setShowArchived }) }) }) }), isCreateChartModalOpen && (_jsx(CreateChartModal, { onClose: closeCreateChartModal, onSuccess: handleCreateChartSuccess }))] }));
};
